import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { X, Download, Share2, Loader2, Award } from "lucide-react";
import { api, apiError, track } from "@/lib/api";
import { toast } from "sonner";

function CertCard({ c, refEl, big = false }) {
  const s = big ? { w: "w-[1080px] h-[1080px] p-[96px]", logo: "text-[64px]", label: "text-[26px]", name: "text-[96px]", quest: "text-[64px]", meta: "text-[30px]", ring: "h-[220px] w-[220px]", icon: 110 }
    : { w: "aspect-square w-full p-7", logo: "text-xl", label: "text-[9px]", name: "text-3xl", quest: "text-xl", meta: "text-[11px]", ring: "h-16 w-16", icon: 32 };
  return (
    <div ref={refEl} className={`flex flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-indigo-700 text-white ${s.w} ${big ? "" : "rounded-[28px]"}`}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex items-center justify-between">
        <span className={`font-display font-extrabold ${s.logo}`}>Nx<span className="text-amber-300">T</span></span>
        <span className={`font-mono text-white/60 ${s.meta}`}>{c.code}</span>
      </div>
      <div>
        <div className={`mx-auto flex items-center justify-center rounded-full border-4 border-amber-300 bg-amber-300/10 ${s.ring}`}><Award size={s.icon} className="text-amber-300" /></div>
        <div className={`mt-6 text-center font-bold uppercase tracking-[0.35em] text-white/60 ${s.label}`}>Career Quest Completed</div>
        <div className={`mt-3 text-center font-display font-extrabold leading-tight ${s.name}`}>{c.name}</div>
        <div className={`mt-4 text-center font-display font-bold text-amber-300 ${s.quest}`}>{c.quest}</div>
        <div className={`mt-3 text-center text-white/70 ${s.meta}`}>{c.levels} levels · {c.duration_days}-day program · +{c.xp} XP</div>
      </div>
      <div className={`flex items-center justify-between border-t border-white/20 pt-4 text-white/60 ${s.meta}`}>
        <span>{c.completed_at}</span><span className="font-bold uppercase tracking-[0.2em]">nxt · know what's next</span>
      </div>
    </div>
  );
}

export function QuestCertificate({ questKey, onClose }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const bigRef = useRef(null);
  useEffect(() => { api.get(`/quests/certificate/${questKey}`).then(({ data }) => setC(data)).catch((e) => { toast.error(apiError(e)); onClose(); }); }, [questKey, onClose]);

  const render = async () => { await document.fonts?.ready; return toPng(bigRef.current, { width: 1080, height: 1080, pixelRatio: 1, cacheBust: true }); };
  const download = async () => {
    setBusy(true);
    try { const url = await render(); const a = document.createElement("a"); a.href = url; a.download = `nxt-certificate-${questKey}.png`; a.click(); track("quest_certificate_downloaded", { quest: questKey }); }
    catch { toast.error("Could not render certificate"); } finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `nxt-certificate-${questKey}.png`, { type: "image/png" });
      const text = `I just completed the "${c.quest}" Career Quest on NxT 🏆 Discover your Career DNA: ${window.location.origin}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], text, title: "My NxT Certificate" });
      else { await navigator.clipboard.writeText(text); toast.success("Share text copied!"); }
      track("quest_certificate_shared", { quest: questKey });
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/80 p-4 backdrop-blur-sm sm:items-center" data-testid="quest-certificate-modal" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
        <div className="mb-3 flex items-center justify-between text-white">
          <span className="font-display font-bold">Your certificate</span>
          <button onClick={onClose} data-testid="certificate-close" className="rounded-full bg-white/10 p-2 hover:bg-white/20"><X className="h-4 w-4" /></button>
        </div>
        {!c ? <div className="flex aspect-square items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div> : (
          <>
            <CertCard c={c} />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={download} disabled={busy} data-testid="certificate-download-btn" className="flex items-center justify-center gap-2 rounded-full bg-amber-400 py-3 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download</button>
              <button onClick={share} disabled={busy} data-testid="certificate-share-btn" className="flex items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-slate-900 hover:bg-white/90 disabled:opacity-60"><Share2 className="h-4 w-4" /> Share</button>
            </div>
            <div className="pointer-events-none fixed left-[-4000px] top-0" aria-hidden="true"><CertCard c={c} refEl={bigRef} big /></div>
          </>
        )}
      </motion.div>
    </div>
  );
}
